/**
 * 阿里百炼 DashScope 应用对话（流式 SSE）。
 * 逻辑放在纯 JS 模块，避免模板里使用小写「ai-chat」标签时被当成未知原生标签，导致 ref 拿到 DOM 而非 Vue 子组件。
 *
 * 代理：开发见 vue.config.js /dashscope-proxy；生产需 Nginx 同源反代。
 */

const DASHSCOPE_API_KEY =
  'sk-c33ad1088c724e98ac899ac5d378028a'
const DASHSCOPE_APP_ID =
  '16394439f9b04a96aa764238667de6df'

/** 与 publicPath / BASE_URL 对齐，避免部署在子目录时仍请求站点根路径而拿不到代理 */
function completionUrl() {
  const base =
    typeof process !== 'undefined' && process.env && process.env.BASE_URL != null
      ? String(process.env.BASE_URL)
      : '/'
  const suffix = `dashscope-proxy/api/v1/apps/${DASHSCOPE_APP_ID}/completion`
  if (base === './') {
    return `./${suffix}`
  }
  const trimmed = base.endsWith('/') ? base.slice(0, -1) : base
  if (trimmed === '' || trimmed === '/') {
    return `/${suffix}`
  }
  return `${trimmed}/${suffix}`
}

let activeController = null

export function abortBailianStream() {
  if (activeController) {
    activeController.abort()
    activeController = null
  }
}

/**
 * @param {string} prompt
 * @param {{ onDelta?: (full: string) => void, onDone?: (full: string) => void, onError?: (msg: string) => void }} handlers
 * @returns {Promise<string>}
 */
export function streamBailianApplication(prompt, handlers = {}) {
  abortBailianStream()
  const ac = new AbortController()
  activeController = ac

  const { onDelta, onDone, onError } = handlers
  let accumulated = ''
  let timedOut = false
  const timeoutMs = 120000
  const timeoutId = setTimeout(() => {
    timedOut = true
    ac.abort()
  }, timeoutMs)

  const finishOk = () => {
    clearTimeout(timeoutId)
    if (activeController === ac) activeController = null
  }

  const url = completionUrl()
  const body = {
    input: { prompt },
    parameters: { incremental_output: true },
    debug: {},
  }

  const applyDataJson = (json) => {
    if (json.code != null && String(json.code) !== '200') {
      const msg = json.message || JSON.stringify(json)
      throw new Error(msg)
    }
    const piece = json.output && json.output.text
    if (piece) {
      // 兼容 incremental_output=false 时每个 chunk 返回已累积全文的情况，避免重复拼接
      if (accumulated.length > 0 && piece.startsWith(accumulated)) {
        accumulated = piece
      } else {
        accumulated += piece
      }
      onDelta && onDelta(accumulated)
    }
  }

  const processDataLine = (line) => {
    const trimmed = line.replace(/\r$/, '')
    if (!trimmed.startsWith('data:')) return
    const raw = trimmed.slice(5).trim()
    if (!raw || raw === '[DONE]') return
    let json
    try {
      json = JSON.parse(raw)
    } catch (e) {
      console.warn('[dashscope] SSE JSON 跳过:', e.message, raw.slice(0, 80))
      return
    }
    applyDataJson(json)
  }

  return fetch(url, {
    method: 'POST',
    signal: ac.signal,
    headers: {
      Authorization: `Bearer ${DASHSCOPE_API_KEY}`,
      'Content-Type': 'application/json',
      'X-DashScope-SSE': 'enable',
    },
    body: JSON.stringify(body),
  })
    .then(async (res) => {
      if (!res.ok) {
        const t = await res.text().catch(() => '')
        throw new Error(t || `HTTP ${res.status}`)
      }

      const ct = (res.headers.get('content-type') || '').toLowerCase()
      if (
        ct.includes('text/html') ||
        (ct.includes('application/json') && !ct.includes('event-stream'))
      ) {
        const text = await res.text()
        if (ct.includes('text/html') || (text.trim().startsWith('<') && text.includes('html'))) {
          const tried = typeof window !== 'undefined' ? new URL(url, window.location.href).href : url
          throw new Error(
            `收到网页而非百炼数据（多为未命中代理、返回了 SPA 的 index.html）。请求地址：${tried}。请用「npm run serve」启动并保存 vue.config.js 后重启 devServer；若已打包部署，请在当前站点路径下为 dashscope-proxy 配置反代到 https://dashscope.aliyuncs.com`
          )
        }
        const json = JSON.parse(text)
        applyDataJson(json)
        finishOk()
        onDone && onDone(accumulated)
        return accumulated
      }

      if (!res.body || !res.body.getReader) {
        throw new Error('当前环境不支持流式读取（ReadableStream）')
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let nl
        while ((nl = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, nl)
          buffer = buffer.slice(nl + 1)
          if (line.length) processDataLine(line)
        }
      }
      buffer += decoder.decode()
      if (buffer.trim()) {
        buffer.split(/\n/).forEach((line) => {
          if (line.trim()) processDataLine(line)
        })
      }

      finishOk()
      onDone && onDone(accumulated)
      return accumulated
    })
    .catch((err) => {
      clearTimeout(timeoutId)
      if (activeController === ac) activeController = null
      if (err.name === 'AbortError') {
        onError && onError(timedOut ? `请求超过 ${timeoutMs / 1000}s 未结束` : '请求已中断')
        return ''
      }
      const msg = err.message || String(err)
      onError && onError(msg)
      return ''
    })
}
